import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, getImageSrc } from "@/lib/api";
import { ShieldCheck, Truck, PhoneCall, ArrowRight, Sprout } from "lucide-react";

const CATEGORIES = [
  { key: "livestock", label: "Livestock", tagline: "Cattle, goats, sheep", img: "https://images.unsplash.com/photo-1744808294458-8c72fea3d6d6?w=900&q=80" },
  { key: "poultry", label: "Poultry", tagline: "Chickens, guinea fowl, eggs", img: "https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?w=900&q=80" },
  { key: "agriculture", label: "Agriculture", tagline: "Maize, cassava, yam, tomatoes", img: "https://images.unsplash.com/photo-1734255026082-82fdc81991f0?w=900&q=80" },
  { key: "fishing", label: "Fishing", tagline: "Tilapia, prawns, smoked fish", img: "https://images.unsplash.com/photo-1712700504489-b3e240144ee3?w=900&q=80" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setFeatured((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1746014929708-fcb859fd3185?w=1800&q=85"
            alt="Ghanaian farm"
            className="w-full h-[520px] object-cover"
          />
          <div className="absolute inset-0 hero-overlay grain" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 pb-16 w-full">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D99B26] text-[#1F2421] text-xs font-bold uppercase tracking-widest" data-testid="hero-badge">
                <Sprout className="w-3.5 h-3.5" /> Sourced locally · Sold nationally
              </span>
              <h1 className="mt-4 text-white font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                From the farms of Ghana <br /> to your <span className="text-[#D99B26]">doorstep</span>.
              </h1>
              <p className="mt-4 text-white/90 text-base sm:text-lg max-w-xl">
                Buy livestock, poultry, crops and fresh fish directly from verified Ghanaian farmers.
                Pay easily with MTN MoMo, Telecel Cash or AT Money.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/marketplace" className="btn-primary px-6 py-3 rounded-xl inline-flex items-center gap-2" data-testid="hero-cta-browse">
                  Browse Marketplace <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/register" className="px-6 py-3 rounded-xl bg-white/95 hover:bg-white text-[#1E4D3B] font-semibold" data-testid="hero-cta-sell">
                  Start selling
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl border border-[#E5D9C8] shadow-sm grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E5D9C8]">
          {[
            { k: "Verified Farmers", v: "Trusted growers, herders & fishermen" },
            { k: "16 Regions", v: "Designed to serve all of Ghana" },
            { k: "Secure Marketplace", v: "Verified sellers · Direct connections" },
            { k: "Mobile Money Payments", v: "MTN MoMo · Telecel Cash · AT Money" },
          ].map((s, i) => (
            <div key={i} className="p-5 text-center">
              <div className="text-xl md:text-2xl font-black text-[#1E4D3B]" data-testid={`stat-${i}`}>{s.k}</div>
              <div className="text-xs uppercase tracking-widest text-[#8C6D53] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Shop by category</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E4D3B] mt-1">What are you sourcing today?</h2>
          </div>
          <Link to="/marketplace" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#C85A32]">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/marketplace?category=${c.key}`} className="group card-farm overflow-hidden" data-testid={`category-card-${c.key}`}>
              <div className="relative h-52 overflow-hidden">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="badge-cat inline-block">{c.label}</div>
                  <div className="text-white font-bold text-lg mt-2">{c.tagline}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Fresh listings</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E4D3B] mt-1">Straight from the farm</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((p) => (
            <Link key={p.id} to={`/product/${p.id}`} className="card-farm overflow-hidden flex flex-col" data-testid={`featured-product-${p.id}`}>
              <div className="h-56 overflow-hidden bg-[#F3ECE2]">
                <img src={getImageSrc(p)} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge-cat">{p.category}</span>
                  <span className="text-xs text-[#5C6660]">{p.region}</span>
                </div>
                <div className="font-bold text-[#1F2421]">{p.name}</div>
                <div className="text-xs text-[#5C6660] mt-1">{p.seller_name}</div>
                <div className="mt-auto pt-3 flex items-baseline justify-between">
                  <div className="text-[#C85A32] font-black text-xl">GH₵ {p.price.toFixed(2)}</div>
                  <div className="text-xs text-[#5C6660]">per {p.unit}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-6 mt-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: ShieldCheck, title: "Verified farmers", desc: "Every seller is verified by region and phone. Trade with confidence." },
          { icon: Truck, title: "Regional delivery", desc: "From Kumasi to Cape Coast. Coordinate delivery directly with sellers." },
          { icon: PhoneCall, title: "MoMo checkout", desc: "MTN, Telecel and AT Money. Simple prompts on your phone." },
        ].map((f, i) => (
          <div key={i} className="card-farm p-6">
            <div className="w-10 h-10 rounded-lg bg-[#F3ECE2] flex items-center justify-center text-[#C85A32]">
              <f.icon className="w-5 h-5" />
            </div>
            <div className="mt-4 font-bold text-lg text-[#1E4D3B]">{f.title}</div>
            <div className="text-sm text-[#5C6660] mt-1">{f.desc}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
